import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { http, formatApiError } from "@/lib/api";
import { Reveal, SectionLabel } from "@/components/Reveal";

const field = "w-full bg-transparent border-b border-[#0B1F3A]/25 py-3 text-base text-[#0B1F3A] placeholder:text-slate-500 focus:outline-none focus:border-[#0f766e] transition-colors";

const Success = ({ onReset }) => {
  const { t } = useLang();
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-[28px] bg-[#0B1F3A] text-white p-10 lg:p-14" data-testid="adhesion-success">
      <motion.div initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}>
        <CheckCircle2 size={56} className="text-[#2DD4BF]" />
      </motion.div>
      <h3 className="mt-8 text-4xl lg:text-5xl font-extrabold uppercase stretch-condensed">{t.form.okTitle}</h3>
      <p className="mt-4 text-lg text-slate-300 max-w-md">{t.form.okText}</p>
      <button onClick={onReset} data-testid="adhesion-new-btn" className="mt-10 rounded-full border border-white/25 px-6 py-3 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-colors">{t.form.again}</button>
    </motion.div>
  );
};

const EMPTY = { name: "", company: "", email: "", phone: "", message: "" };

export const Join = () => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/adhesions", { ...form, lang });
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t.form.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="join" className="relative bg-[#EEF1EC] text-[#0B1F3A] py-24 lg:py-36" data-testid="join-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <SectionLabel dark>{t.form.label}</SectionLabel>
          <h2 className="mt-6 text-5xl lg:text-7xl font-extrabold uppercase stretch-condensed leading-[0.88] tracking-tight">{t.form.title}</h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-md">{t.form.sub}</p>
          <dl className="mt-12 grid grid-cols-2 gap-6 border-t border-[#0B1F3A]/15 pt-8 text-sm">
            <div>
              <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t.form.provider}</dt>
              <dd className="mt-2 font-semibold">bsys<span className="text-[#2DD4BF]">.</span></dd>
              <dd className="text-slate-600">Regis Bermudez</dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t.form.contact}</dt>
              <dd className="mt-2"><a href="mailto:contato@bsys.ca" data-testid="contact-email" className="inline-flex items-center gap-2 font-semibold hover:text-[#0f766e] transition-colors"><Mail size={14} />contato@bsys.ca</a></dd>
            </div>
          </dl>
        </Reveal>
        <div className="lg:col-span-7">
          <AnimatePresence initial={false}>
            {done ? (
              <Success key="ok" onReset={() => setDone(false)} />
            ) : (
              <Reveal key="form">
                <form onSubmit={submit} className="space-y-8" data-testid="adhesion-form">
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                    <input required minLength={2} className={field} placeholder={t.form.name} value={form.name} onChange={set("name")} data-testid="input-name" />
                    <input required className={field} placeholder={t.form.company} value={form.company} onChange={set("company")} data-testid="input-company" />
                    <input required type="email" className={field} placeholder={t.form.email} value={form.email} onChange={set("email")} data-testid="input-email" />
                    <input className={field} placeholder={t.form.phone} value={form.phone} onChange={set("phone")} data-testid="input-phone" />
                  </div>
                  <textarea rows={3} className={`${field} resize-none`} placeholder={t.form.message} value={form.message} onChange={set("message")} data-testid="input-message" />
                  <button type="submit" disabled={busy} data-testid="adhesion-submit-btn"
                    className="group w-full sm:w-auto inline-flex items-center justify-between gap-6 rounded-full bg-[#0B1F3A] text-white pl-7 pr-2 py-2 font-semibold hover:bg-[#132D54] disabled:opacity-60 transition-colors">
                    {busy ? t.form.sending : t.form.submit}
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-[#2DD4BF] text-[#0B1F3A] transition-transform duration-500 group-hover:translate-x-1"><ArrowRight size={16} /></span>
                  </button>
                </form>
              </Reveal>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
